import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { db, functions } from "../lib/firebase";
import {
  collection, query, where, orderBy, limit, getDocs, doc, getDoc, Timestamp
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

// Le champ `date` Firestore est stocké à minuit UTC du jour J. Pour décider
// si un atelier est passé, on doit le combiner avec son `endTime` (string
// "HH:MM" en heure locale Paris). Un atelier daté du jour J avec endTime
// "11:30" est considéré "à venir" tant qu'on n'est pas après 11h30 locale.
export function atelierEndAt(atelier) {
  if (!atelier?.date) return null;
  const d = atelier.date.toDate ? atelier.date.toDate() : new Date(atelier.date);
  // On reconstruit l'instant local du jour J à `endTime` (ou 23:59 par
  // défaut). NB : utilise l'heure locale du runtime — acceptable pour des
  // clients FR. Pour un client à l'étranger l'offset peut décaler d'1-2h
  // mais ça reste cohérent avec son fuseau perçu.
  let h = 23, m = 59;
  if (typeof atelier.endTime === 'string' && /^\d{1,2}:\d{2}$/.test(atelier.endTime)) {
    const [hh, mm] = atelier.endTime.split(':').map(Number);
    if (Number.isFinite(hh)) h = hh;
    if (Number.isFinite(mm)) m = mm;
  }
  const local = new Date(d);
  local.setHours(h, m, 0, 0);
  return local;
}

export const useAteliersStore = defineStore("ateliers", () => {
  const upcomingAteliers = ref([]);
  const pastAteliers = ref([]);
  const myInscriptions = ref([]);
  const currentAtelier = ref(null);
  const loading = ref(false);

  async function fetchUpcoming(max = 10) {
    loading.value = true;
    try {
      // Query large par date (hier) pour ne pas exclure l'atelier du jour J
      // dont la date Firestore est à minuit UTC. Le filtre fin se fait
      // côté client via `atelierEndAt(a) >= now`.
      const startOfYesterday = new Date();
      startOfYesterday.setHours(0, 0, 0, 0);
      startOfYesterday.setDate(startOfYesterday.getDate() - 1);
      const q = query(
        collection(db, "ateliers"),
        where("status", "in", ["published", "announced"]),
        where("date", ">=", Timestamp.fromDate(startOfYesterday)),
        orderBy("date", "asc"),
        limit(max + 5)
      );
      const snap = await getDocs(q);
      const now = Date.now();
      upcomingAteliers.value = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((a) => {
          const endAt = atelierEndAt(a);
          return endAt && endAt.getTime() >= now;
        })
        .slice(0, max);
    } finally {
      loading.value = false;
    }
  }

  async function fetchPast(max = 20) {
    // Indépendant du cron `markPastAteliers`. Query par date (demain) pour
    // capturer aussi l'atelier du jour J s'il est déjà terminé. Le filtre
    // précis se fait côté client via `atelierEndAt(a) < now`.
    const startOfTomorrow = new Date();
    startOfTomorrow.setHours(0, 0, 0, 0);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const q = query(
      collection(db, "ateliers"),
      where("date", "<", Timestamp.fromDate(startOfTomorrow)),
      orderBy("date", "desc"),
      limit(max * 2)
    );
    const snap = await getDocs(q);
    const now = Date.now();
    pastAteliers.value = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((a) => ["past", "published", "announced"].includes(a.status))
      .filter((a) => {
        const endAt = atelierEndAt(a);
        return endAt && endAt.getTime() < now;
      })
      .slice(0, max);
  }

  // Dernier atelier passé dans la fenêtre `daysAgo` (par défaut 2 jours).
  // Utilisé par la home pour garder l'atelier en vitrine quelques jours
  // après sa date afin que la section ne se vide pas brutalement.
  async function fetchLatestRecentPast(daysAgo = 2) {
    const now = Date.now();
    const start = new Date(now - (daysAgo + 1) * 24 * 60 * 60 * 1000);
    const startOfTomorrow = new Date();
    startOfTomorrow.setHours(0, 0, 0, 0);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const q = query(
      collection(db, "ateliers"),
      where("date", ">=", Timestamp.fromDate(start)),
      where("date", "<", Timestamp.fromDate(startOfTomorrow)),
      orderBy("date", "desc"),
      limit(10)
    );
    const snap = await getDocs(q);
    const cutoff = now - daysAgo * 24 * 60 * 60 * 1000;
    const found = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((a) => ["past", "published", "announced"].includes(a.status))
      .find((a) => {
        const endAt = atelierEndAt(a);
        if (!endAt) return false;
        const t = endAt.getTime();
        return t < now && t >= cutoff;
      });
    return found || null;
  }

  async function fetchBySlug(slug) {
    loading.value = true;
    try {
      const q = query(collection(db, "ateliers"), where("slug", "==", slug), limit(1));
      const snap = await getDocs(q);
      currentAtelier.value = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
      return currentAtelier.value;
    } finally {
      loading.value = false;
    }
  }

  async function fetchById(id) {
    const snap = await getDoc(doc(db, "ateliers", id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async function fetchMyInscriptions(uid) {
    const q = query(
      collection(db, "ateliers_inscriptions"),
      where("userId", "==", uid),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    myInscriptions.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function fetchInscriptionsByAtelier(atelierId) {
    const q = query(
      collection(db, "ateliers_inscriptions"),
      where("atelierId", "==", atelierId),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function createPaymentIntent({ atelierId, seats, participantNames }) {
    const fn = httpsCallable(functions, "createAtelierPaymentIntent");
    const res = await fn({ atelierId, seats, participantNames });
    return res.data;
  }

  async function confirmInscription(inscriptionId) {
    const fn = httpsCallable(functions, "confirmAtelierInscription");
    const res = await fn({ inscriptionId });
    return res.data;
  }

  async function releasePendingInscription(inscriptionId) {
    const fn = httpsCallable(functions, "releaseMyPendingInscription");
    const res = await fn({ inscriptionId });
    return res.data;
  }

  async function cancelMyInscription(inscriptionId) {
    const fn = httpsCallable(functions, "cancelMyAtelierInscription");
    const res = await fn({ inscriptionId });
    return res.data;
  }

  async function adminUpsertAtelier(payload) {
    const fn = httpsCallable(functions, "adminUpsertAtelier");
    const res = await fn(payload);
    return res.data;
  }

  async function adminCancelInscription({ inscriptionId, refund, reason }) {
    const fn = httpsCallable(functions, "adminCancelAtelierInscription");
    const res = await fn({ inscriptionId, refund, reason });
    return res.data;
  }

  async function adminCancelAtelier({ atelierId, reason }) {
    const fn = httpsCallable(functions, "adminCancelAtelier");
    const res = await fn({ atelierId, reason });
    return res.data;
  }

  async function adminDeleteAtelier({ atelierId, force = false }) {
    const fn = httpsCallable(functions, "adminDeleteAtelier");
    const res = await fn({ atelierId, force });
    return res.data;
  }

  async function fetchAllAdmin() {
    const q = query(collection(db, "ateliers"), orderBy("date", "desc"));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  const hasUpcoming = computed(() => upcomingAteliers.value.length > 0);

  return {
    upcomingAteliers, pastAteliers, myInscriptions, currentAtelier, loading,
    hasUpcoming,
    fetchUpcoming, fetchPast, fetchLatestRecentPast, fetchBySlug, fetchById,
    fetchMyInscriptions, fetchInscriptionsByAtelier, fetchAllAdmin,
    createPaymentIntent, confirmInscription, releasePendingInscription, cancelMyInscription,
    adminUpsertAtelier, adminCancelInscription, adminCancelAtelier, adminDeleteAtelier,
  };
});
